import { UpgradeBanner } from "./SubscriptionUI";
import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import { useUnsavedGuard } from "../lib/unsavedGuard";
import { 
  LayoutDashboard, 
  Users, 
  Clock, 
  DollarSign, 
  Calendar,
  Settings,
  LogOut,
  User,
  Menu,
  X,
  Briefcase,
  CalendarClock,
  UsersRound,
  ScrollText,
  Shield,
  Eye,
  Mail,
  Sparkles,
  Crown,
  BarChart3,
  Rocket,
  Zap,
  TrendingUp,
  Bell,
  Star,
  Package,
  Building2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import ChangeEmailDialog from "./ChangeEmailDialog";
import IdleTimeoutGuard from "./IdleTimeoutGuard";
import { RoadmapIcon } from "../lib/roadmapIcons";

const Layout = ({ children }) => {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const { attemptNavigate } = useUnsavedGuard();
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  const [roadmapItems, setRoadmapItems] = useState({ coming_soon: [], just_launched: [] });

  // Poll pending-review count every 60s for live red dot in sidebar
  useEffect(() => {
    if (!user) return;
    // Bail when there is no tenant context — covers super admins (tenant_id=null),
    // brand-new signups, and any half-hydrated auth state. Without this, the
    // /api/.../pending-review call returns 400 "No tenant associated".
    if (!user.tenant_id) return;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const url = user.role === "employee" ? "/me/time-entries/pending-review" : "/time-entries/pending-review";
        const res = await apiClient.get(url);
        if (!cancelled) setPendingReviewCount(res.data?.count || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user, location.pathname]);

  // Fetch roadmap items so the sidebar Coming Soon section is Super-Admin-driven.
  useEffect(() => {
    if (!user || !user.tenant_id) return;
    let cancelled = false;
    apiClient.get("/roadmap/items").then((res) => {
      if (!cancelled) setRoadmapItems(res.data || { coming_soon: [], just_launched: [] });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  // Super admin (not impersonating) belongs in the admin workspace, not owner views.
  // We detect an in-flight impersonation by the `imp_` session token prefix — that
  // survives page reloads but is truthier than `admin_original_token`, which can go
  // stale if a previous "Stop Impersonating" flow crashed mid-cleanup and leave the
  // super admin trapped in a tenant view with no escape (user-reported regression).
  useEffect(() => {
    const sessionToken = localStorage.getItem("session_token") || "";
    const hasOriginalToken = !!localStorage.getItem("admin_original_token");
    const midImpersonation = sessionToken.startsWith("imp_");

    // Clean up stale admin_original_token from a previous failed stop-impersonation.
    if (hasOriginalToken && !midImpersonation) {
      localStorage.removeItem("admin_original_token");
    }

    if (user?.is_super_admin && !user?.impersonating && !midImpersonation &&
        !location.pathname.startsWith("/admin")) {
      navigate("/admin", { replace: true });
    }
  }, [user, location.pathname, navigate]);

  const handleLogout = async () => {
    attemptNavigate(async () => {
      await logout();
      navigate('/');
    });
  };

  const isOwnerOrManager = user?.role === "owner" || user?.role === "manager";
  const isOwner = user?.role === "owner";
  const plan = tenant?.plan || "free";
  const planRank = { free: 0, starter: 1, pro: 2 };

  // Nav items include a `requiresPlan` when they're gated. We keep them visible
  // for lower tiers with an "Upgrade" pill so users discover the feature and
  // navigate to it — the destination page shows its own upgrade banner.
  const navItems = isOwnerOrManager
    ? [
        { to: "/dashboard", icon: <LayoutDashboard className="w-5 h-5" />, label: "Dashboard" },
        { to: "/employees", icon: <Users className="w-5 h-5" />, label: "Employees" },
        { to: "/pay-groups", icon: <Briefcase className="w-5 h-5" />, label: "Pay Groups" },
        { to: "/time-entries", icon: <Clock className="w-5 h-5" />, label: "Time Entries" },
        { to: "/payments", icon: <DollarSign className="w-5 h-5" />, label: "Payments" },
        { to: "/shifts", icon: <CalendarClock className="w-5 h-5" />, label: "Schedules & Shifts" },
        { to: "/schedule", icon: <Calendar className="w-5 h-5" />, label: "Schedule" },
        { to: "/team", icon: <UsersRound className="w-5 h-5" />, label: "Team" },
        { to: "/audit-log", icon: <ScrollText className="w-5 h-5" />, label: "Audit Log", requiresPlan: "pro" },
        ...(isOwner ? [{ to: "/settings", icon: <Settings className="w-5 h-5" />, label: "Settings" }] : []),
      ]
    : [
        // Employee-only navigation
        { to: "/dashboard", icon: <LayoutDashboard className="w-5 h-5" />, label: "Dashboard" },
        { to: "/time-entries", icon: <Clock className="w-5 h-5" />, label: "My Time" },
        { to: "/my-payments", icon: <DollarSign className="w-5 h-5" />, label: "My Payments" },
        { to: "/schedule", icon: <Calendar className="w-5 h-5" />, label: "My Schedule" },
      ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50
        w-64 lg:h-screen bg-[#0A192F] text-white
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo — Nirvanta brand + tenant identity below */}
          <div className="p-6 border-b border-[#233554]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0D9488] rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold font-['Manrope'] tracking-tight">Nirvanta</h1>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Workforce Pay</p>
              </div>
            </div>
            {/* Tenant identity — small circle logo above the company name.
                Falls back to a building icon when no logo has been uploaded.
                See LogoUploader.jsx for the uploader/preview components. */}
            {tenant && (
              <div className="mt-4 flex flex-col items-center text-center" data-testid="sidebar-tenant-identity">
                <div className="w-8 h-8 rounded-full bg-white/95 border border-slate-200 flex items-center justify-center overflow-hidden">
                  {tenant.logo ? (
                    <img src={tenant.logo} alt="" className="max-w-full max-h-full object-contain" data-testid="sidebar-tenant-logo" />
                  ) : (
                    <Building2 className="w-4 h-4 text-slate-400" />
                  )}
                </div>
                <p className="mt-1.5 text-xs text-slate-300 truncate max-w-[160px]" data-testid="sidebar-company-name">
                  {tenant.company_name}
                </p>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const gated = item.requiresPlan && planRank[plan] < planRank[item.requiresPlan];
              const pillLabel = item.requiresPlan === "pro" ? "Pro" : item.requiresPlan === "starter" ? "Starter" : null;
              return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-3 rounded-lg
                  transition-colors duration-200
                  ${isActive
                    ? 'bg-[#0D9488] text-white'
                    : gated
                      ? 'text-slate-400 hover:bg-[#112240] hover:text-white'
                      : 'text-slate-300 hover:bg-[#112240] hover:text-white'
                  }
                `}
                onClick={(e) => {
                  if (location.pathname === item.to) { setSidebarOpen(false); return; }
                  e.preventDefault();
                  attemptNavigate(() => { navigate(item.to); setSidebarOpen(false); });
                }}
              >
                {item.icon}
                <span className="font-medium flex-1">{item.label}</span>
                {item.to === "/time-entries" && pendingReviewCount > 0 && (
                  <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold" data-testid="time-entries-pending-badge">
                    {pendingReviewCount > 9 ? "9+" : pendingReviewCount}
                  </span>
                )}
                {gated && pillLabel && (
                  <span
                    className={`ml-auto inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                      item.requiresPlan === "pro"
                        ? "bg-amber-500/20 text-amber-300"
                        : "bg-blue-500/20 text-blue-300"
                    }`}
                    data-testid={`nav-upgrade-pill-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {item.requiresPlan === "pro" ? <Crown className="w-2.5 h-2.5" /> : <Sparkles className="w-2.5 h-2.5" />}
                    {pillLabel}
                  </span>
                )}
              </NavLink>
            );})}

            {isOwner && (roadmapItems.coming_soon.length > 0 || roadmapItems.just_launched.length > 0) && (
              <div className="pt-6" data-testid="coming-soon-section">
                <div className="px-4 pb-2 flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                  <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                    {roadmapItems.just_launched.length > 0 ? "What's New" : "Coming Soon"}
                  </span>
                </div>
                {roadmapItems.just_launched.map((item) => (
                  <button
                    key={item.roadmap_item_id}
                    type="button"
                    data-testid={`just-launched-${item.roadmap_item_id}`}
                    onClick={() => attemptNavigate(() => { navigate("/settings?tab=roadmap"); setSidebarOpen(false); })}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-emerald-200 hover:bg-[#112240] transition-colors duration-200 text-left"
                  >
                    <span className="opacity-90"><RoadmapIcon iconKey={item.icon} /></span>
                    <span className="font-medium flex-1 text-sm">{item.title}</span>
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                      New
                    </span>
                  </button>
                ))}
                {roadmapItems.coming_soon.map((item) => (
                  <button
                    key={item.roadmap_item_id}
                    type="button"
                    data-testid={`coming-soon-${item.roadmap_item_id}`}
                    onClick={() => attemptNavigate(() => { navigate("/settings?tab=roadmap"); setSidebarOpen(false); })}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-[#112240] hover:text-slate-200 transition-colors duration-200 text-left"
                  >
                    <span className="opacity-60"><RoadmapIcon iconKey={item.icon} /></span>
                    <span className="font-medium flex-1 text-sm">{item.title}</span>
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">Soon</span>
                  </button>
                ))}
              </div>
            )}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-[#233554]">
            <div className="flex items-center gap-3 px-4 py-3">
              <Avatar className="w-10 h-10">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-[#0D9488] text-white">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => { navigate("/account"); setSidebarOpen(false); }}
              data-testid="account-settings-btn"
              className="w-full mt-1 text-slate-300 hover:text-white hover:bg-[#112240] justify-start text-sm"
            >
              <User className="w-4 h-4 mr-2" />
              Account settings
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full mt-1 text-slate-300 hover:text-white hover:bg-[#112240] justify-start"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      <ChangeEmailDialog open={showChangeEmail} onOpenChange={setShowChangeEmail} />
      <IdleTimeoutGuard />

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <UpgradeBanner />
        {/* Top bar - mobile */}
        {/* Impersonation Banner */}
        {user?.impersonating && (
          (() => {
            const isEmployee = user.role === "employee";
            const barBg = isEmployee ? "bg-red-500 text-red-50 border-red-600" : "bg-amber-500 text-amber-950 border-amber-600";
            const btnStyle = isEmployee ? "bg-white border-red-700 text-red-800 hover:bg-red-50" : "bg-white border-amber-700 text-amber-900 hover:bg-amber-50";
            return (
              <div className={`${barBg} px-4 py-2 flex items-center justify-between sticky top-0 z-40 border-b-2`} data-testid="impersonation-banner">
                <div className="flex items-center gap-2 text-sm">
                  <Eye className="w-4 h-4" />
                  <span>
                    <span className="font-semibold">Impersonating</span> as {user.email}
                    {user.role && <span className="ml-1 uppercase text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-white/80 text-slate-800">{user.role}</span>}
                    <span className="opacity-70 ml-1">(super admin: {user.impersonator_email})</span>
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className={`${btnStyle} h-7`}
                  onClick={async () => {
                    try {
                      await apiClient.post('/admin/impersonate/stop');
                    } catch { /* ignore */ }
                    const orig = localStorage.getItem('admin_original_token');
                    const returnPath = localStorage.getItem('admin_return_path');
                    if (orig) {
                      localStorage.setItem('session_token', orig);
                      localStorage.removeItem('admin_original_token');
                    } else {
                      localStorage.removeItem('session_token');
                    }
                    localStorage.removeItem('admin_return_path');
                    // Return to the admin page they were on (tenant detail, tenants list, etc.)
                    // instead of always dropping them on /admin — preserves context.
                    window.location.href = returnPath && returnPath.startsWith('/admin') ? returnPath : '/admin';
                  }}
                  data-testid="stop-impersonating-btn"
                >
                  Stop Impersonating
                </Button>
              </div>
            );
          })()
        )}

        {/* Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-30">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#0D9488] rounded-lg flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-[#0A192F] font-['Manrope']">Nirvanta</span>
            </div>
            <Avatar className="w-8 h-8">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-[#0D9488] text-white text-sm">
                {user?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">
          {children}
        </main>
        <footer className="border-t border-slate-200 px-4 py-3 text-center">
          <p className="text-xs text-slate-400">© 2025 Marai Software Tech. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default Layout;
